import React from 'react';

import InfiniteScrollTable from 'components/InfiniteScrollTable';
import TableContainer      from 'components/TableContainer';
import TableToolbar        from './TableToolbar';
import TopUpDialog         from './topUpDialog';

import columnOptions           from './cardsTableColumnOptions';
import createRestRequestConfig from 'rest/createRestRequestConfig';
import prepareColumns          from 'utils/prepareColumns';
import useStyles               from './cardsTableStyles';

import { AuthContext }             from 'App';
import { CARD_STATUSES_NS }        from 'i18n';
import { DATA_TABLE_NS }           from '../const';
import { FINTECH_PATHS }           from 'rest/const';
import useCardsTableButtonsHandler from './useCardsTableButtonsHandler';
import { useTranslation }          from 'react-i18next';


function prepareFilterData(data) {
  if (data == null || data.length === 0) { //null or undefined or empty string
    return undefined; //undefined values are not inserted to URL as query params
  }
  return data;
}

function generateFilterData(cardIdFilterData, statusFilterData) {
  return {
    //Keys of this object have to be equal to parameter names in REST
    card_key: prepareFilterData(cardIdFilterData),
    status:   prepareFilterData(statusFilterData),
  };
}


export default function CardsTable() {

  const { classes }  = useStyles();
  const { t }        = useTranslation(DATA_TABLE_NS);
  const cardStatuses = useTranslation(CARD_STATUSES_NS);

  const { userInfo } = React.useContext(AuthContext);
  const axiosCfg     = createRestRequestConfig(userInfo.accessToken, true);

  const [alert, setAlert] = React.useState(undefined);

  const [filtersExpanded,  setFiltersExpanded]  = React.useState(false);
  const [cardIdFilterData, setCardIdFilterData] = React.useState(undefined);
  const [statusFilterData, setStatusFilterData] = React.useState(undefined);

  const [filterData, setFilterData] = React.useState(
    () => generateFilterData(cardIdFilterData, statusFilterData)
  );

  const [justMounted, setJustMounted] = React.useState(true);
  React.useEffect(() => {
    if (justMounted) { // Skip running onMount
      setJustMounted(false);
      return;
    }
    const newFilterData = generateFilterData(cardIdFilterData, statusFilterData);
    const debounce = setTimeout(() => setFilterData(newFilterData), 1000);
    return () => clearTimeout(debounce);
    // useRef keeps state when Component is unmounted, as a result it can't be used there
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardIdFilterData, statusFilterData]);

  /**
   * Special buttons inside of table
   */

  const [rowWithCardToActivate, setRowWithCardToActivate] = React.useState(null); //Activate card from this row
  function activateCard(row) {
    setRowWithCardToActivate(row);
  }
  const [cardToTopUp,   setCardToTopUp]   = React.useState(null); //Use this card in Dialog
  const [topUpCardBody, setTopUpCardBody] = React.useState(null); //This is going to be body of POST
  function topUpCard(row) {
    setCardToTopUp(row.original);
  }

  useCardsTableButtonsHandler(
    setAlert, axiosCfg, //Common props
    rowWithCardToActivate, setRowWithCardToActivate, //Card activation props
    topUpCardBody, setTopUpCardBody, //Card top upping props
  );

  const columns = React.useMemo(
    () => prepareColumns(
      t('columnNames', { returnObjects: true }),
      columnOptions(t, classes, activateCard, topUpCard, cardStatuses)
    ), [t, classes, cardStatuses]
  );

  return (
    <TableContainer title={t('title')}>
      <InfiniteScrollTable
        key={rowWithCardToActivate?.cardId /* This way it redraws table to display new status */}
        tableTL={t}
        columns={columns}
        queryParams={filterData}
        pathFormat={FINTECH_PATHS.cardsInfo}
        alert={alert}
        setAlert={setAlert}
        renderTopToolbar={() =>
          <TableToolbar
            filtersExpanded={filtersExpanded}
            setFiltersExpanded={setFiltersExpanded}
            cardIdFilterData={cardIdFilterData}
            setCardIdFilterData={setCardIdFilterData}
            statusFilterData={statusFilterData}
            setStatusFilterData={setStatusFilterData}
          />
        }
      />
      {cardToTopUp &&
        <TopUpDialog
          card={cardToTopUp}
          closeDialog={() => setCardToTopUp(null)}
          onSubmit={(card) => setTopUpCardBody(card)}
        />
      }
    </TableContainer>
  );
}
